import React, { useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiChevronDown,
  FiCheck,
  FiRefreshCcw,
} from "react-icons/fi";
import { FaLungs, FaRegCommentDots, FaRegLightbulb } from "react-icons/fa";
import { MdSelfImprovement } from "react-icons/md";
import { getBreathingHistory } from "../Api/breathingApi";
import { getUserStreak } from "../Api/userApi";
import { getFreezeAllowance } from "../utils/plan";
import { useLocation, useNavigate } from "react-router-dom";

const getHistoryPayload = (response) => {
  const payload = response?.data?.data || response?.data || {};
  return Array.isArray(payload) ? payload : payload.history || payload.sessions || [];
};

const formatDuration = (seconds = 0) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes && remainingSeconds) return `${minutes}m ${remainingSeconds}s`;
  if (minutes) return `${minutes}m`;
  return `${remainingSeconds}s`;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const toDateInputValue = (value) => {
  if (!value) return "";

  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTechniqueLabel = (technique) => {
  const labels = {
    "4-7-8": "4-7-8 Breathing",
    "4-4-8": "4-4-8 Breathing",
    "5-2-7": "5-2-7 Breathing",
    triangle: "4-7-8 Breathing",
    "4-4-4-4": "Box Breathing",
    "5-2-7-2": "5-2-7-2 Box Breathing",
    "4-4-6-2": "4-4-6-2 Box Breathing",
    box: "Box Breathing",
    "4-4": "4-4 Breathing",
    "5-7": "5-7 Breathing",
    "4-6": "4-6 Breathing",
    circle: "4-4 Breathing",
  };

  return labels[technique] || technique || "Breathing";
};

const PERIOD_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "last_3_months", label: "Last 3 Months" },
  { id: "this_year", label: "This Year" },
  { id: "all", label: "All Time" },
];

const getPeriodDateRange = (period) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  switch (period) {
    case "today": {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_week": {
      const day = now.getDay();
      const diffToMonday = (day === 0 ? -6 : 1) - day;
      start.setDate(now.getDate() + diffToMonday);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_week": {
      const day = now.getDay();
      const diffToLastMonday = ((day === 0 ? -6 : 1) - day) - 7;
      start.setDate(now.getDate() + diffToLastMonday);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_month": {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_month": {
      start.setMonth(now.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "last_3_months": {
      start.setMonth(now.getMonth() - 3, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "this_year": {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case "all":
    default:
      return null;
  }
};

const CustomPeriodDropdown = ({ selectedPeriod, onSelectPeriod }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeOption = PERIOD_OPTIONS.find((opt) => opt.id === selectedPeriod) || PERIOD_OPTIONS[0];

  return (
    <div className="relative inline-block text-left z-30" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Period: ${activeOption.label}`}
        className="inline-flex items-center justify-between gap-3 rounded-xl bg-[#f4f8f1] border border-[#e1eadb] px-3.5 py-2 min-h-[44px] sm:min-h-[40px] text-sm sm:text-xs font-black text-[#22331b] shadow-sm hover:bg-[#eef6ea] transition-all cursor-pointer min-w-[145px]"
      >
        <span className="flex items-center gap-2">
          <FiCalendar className="text-[#7d9667] text-sm" />
          {activeOption.label}
        </span>
        <FiChevronDown
          className={`text-[#7d9667] text-sm transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-52 max-w-[calc(100vw-2rem)] max-h-[60dvh] overflow-y-auto rounded-2xl bg-white border border-[#e8efe3] shadow-2xl shadow-[#7d9667]/30 z-[100] py-1.5 overflow-hidden">
          {PERIOD_OPTIONS.map((opt) => {
            const isSelected = selectedPeriod === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  onSelectPeriod(opt.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-2.5 min-h-[44px] sm:min-h-0 text-sm sm:text-xs font-bold transition-colors cursor-pointer ${
                  isSelected
                    ? "bg-[#f4f8f1] text-[#7d9667] font-black"
                    : "text-[#3a4a31] hover:bg-[#fbfdf8]"
                }`}
              >
                <span>{opt.label}</span>
                {isSelected && <FiCheck className="text-[#7d9667] text-sm" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ==========================================
   DAILY XP BREAKDOWN COMPONENT
   ========================================== */
// XP earned per completed practice. Colors are shared with the Wellness Overview rings
// (validated for colorblind separation on white; gold is low-contrast, so every slice is labelled).
const XP_ACTIVITIES = [
  { key: "intention", label: "Intention", xp: 5, color: "#e3a92b", tint: "#fdf6e7", Icon: FaRegLightbulb },
  { key: "breathing", label: "Breathing", xp: 15, color: "#2b93b3", tint: "#eaf5f8", Icon: FaLungs },
  { key: "meditation", label: "Meditation", xp: 15, color: "#6a3f8c", tint: "#f3eef7", Icon: MdSelfImprovement },
  { key: "affirmation", label: "Affirmations", xp: 20, color: "#4a7f2e", tint: "#eef5ea", Icon: FaRegCommentDots },
];

const DAILY_XP_GOAL = XP_ACTIVITIES.reduce((sum, activity) => sum + activity.xp, 0);

const DailyXpCard = ({ counts }) => {
  const [activeKey, setActiveKey] = useState(null);

  const rows = XP_ACTIVITIES.map((activity) => {
    const count = counts[activity.key] || 0;
    return { ...activity, count, earned: count * activity.xp };
  });
  const totalXp = rows.reduce((sum, row) => sum + row.earned, 0);
  const activeRow = rows.find((row) => row.key === activeKey);

  const toggleActive = (key) => setActiveKey((current) => (current === key ? null : key));

  return (
    <div className="rounded-[28px] border bg-white border-[#e8efe3] text-[#22331b] shadow-[0_10px_30px_rgba(80,105,67,0.06)] p-4 sm:p-6 md:p-7 relative overflow-hidden">
      <div className="absolute -left-20 -top-20 w-60 h-60 rounded-full blur-3xl pointer-events-none bg-[#7d9667]/10" />
      <div className="absolute -right-20 -bottom-20 w-60 h-60 rounded-full blur-3xl pointer-events-none bg-sky-200/20" />

      <div className="relative flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[#e8efe3]">
        <div className="min-w-0">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7d9667]">MentalSwasthya • Daily XP</span>
          <h2 className="text-lg sm:text-xl font-black tracking-tight mt-0.5 text-[#22331b]">TODAY'S XP BREAKDOWN</h2>
        </div>
      </div>

      <div className="relative grid grid-cols-1 items-center gap-4 pt-5 sm:pt-6">
        {/* Four-slice XP wheel: one quadrant per practice, filled when earned today */}
        <div className="w-full max-w-[340px] mx-auto">
          {(() => {
            const cx = 100, cy = 100, rOut = 94, rIn = 46;
            const pt = (r, deg) => {
              const rad = ((deg - 90) * Math.PI) / 180;
              return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
            };
            const sector = (start, end) => {
              const [x1, y1] = pt(rOut, start), [x2, y2] = pt(rOut, end);
              const [x3, y3] = pt(rIn, end), [x4, y4] = pt(rIn, start);
              return `M${x1} ${y1} A${rOut} ${rOut} 0 0 1 ${x2} ${y2} L${x3} ${y3} A${rIn} ${rIn} 0 0 0 ${x4} ${y4} Z`;
            };
            // clockwise from 12 o'clock; text anchors for the outside labels
            const quads = [
              { key: "breathing", start: 0, end: 90 },
              { key: "affirmation", start: 90, end: 180 },
              { key: "meditation", start: 180, end: 270 },
              { key: "intention", start: 270, end: 360 },
            ].map((q) => ({ ...q, row: rows.find((r) => r.key === q.key) }));
            const byKey = (k) => quads.find((q) => q.key === k).row;
            const OutsideLabel = ({ row, align }) => (
              <div className={`flex flex-col ${align === "right" ? "items-end text-right" : "items-start text-left"}`}>
                <span className="text-xs sm:text-sm font-bold text-[#22331b]">{row.label}</span>
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-500 leading-tight">{row.xp} XP/session</span>
                <span className="text-base mt-0.5" style={{ color: row.color }}><row.Icon aria-hidden="true" /></span>
              </div>
            );
            return (
              <>
                <div className="flex justify-between px-1">
                  <OutsideLabel row={byKey("intention")} align="left" />
                  <OutsideLabel row={byKey("breathing")} align="right" />
                </div>
                <div className="relative w-full aspect-square -my-2">
                  <svg viewBox="0 0 200 200" className="w-full h-full" role="img"
                    aria-label={`Today's XP: ${totalXp} total. ${rows.map((row) => `${row.label} ${row.earned} XP`).join(", ")}.`}>
                    {quads.map(({ key, start, end, row }) => {
                      const mid = (start + end) / 2;
                      const [tx, ty] = pt((rOut + rIn) / 2, mid);
                      const done = row.earned > 0;
                      const dim = activeKey && activeKey !== key;
                      return (
                        <g
                          key={key}
                          className="cursor-pointer"
                          opacity={dim ? 0.45 : 1}
                          onMouseEnter={() => setActiveKey(key)}
                          onMouseLeave={() => setActiveKey(null)}
                          onClick={() => toggleActive(key)}
                          style={{ transition: "opacity 0.2s ease" }}
                        >
                          <path d={sector(start + 0.6, end - 0.6)} fill={row.color} fillOpacity={done ? 1 : 0.28} stroke="#ffffff" strokeWidth="1.5" />
                          <text x={tx} y={ty + 6} textAnchor="middle" fontSize="16" fontWeight="800" fill={done ? "#ffffff" : "#22331b"}>
                            {row.earned}
                          </text>
                        </g>
                      );
                    })}
                  </svg>

                  {/* Centre readout (shows the hovered / tapped practice) */}
                  <div className="absolute inset-[30%] rounded-full bg-white shadow-[0_6px_24px_rgba(80,105,67,0.18)] border border-[#eef2ea] flex flex-col items-center justify-center text-center pointer-events-none" aria-live="polite">
                    <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-[#66785c] leading-tight">
                      {activeRow ? activeRow.label : "Daily Total"}
                    </span>
                    <span className="text-2xl sm:text-3xl font-black leading-none text-[#22331b] mt-0.5">
                      {activeRow ? activeRow.earned : totalXp}
                    </span>
                    <span className="text-[10px] font-bold text-[#66785c]">XP</span>
                  </div>
                </div>
                <div className="flex justify-between px-1">
                  <OutsideLabel row={byKey("meditation")} align="left" />
                  <OutsideLabel row={byKey("affirmation")} align="right" />
                </div>
              </>
            );
          })()}
        </div>

        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#66785c] mt-2 text-center">
            {totalXp === 0
              ? `Complete any practice today to start earning XP. Finish all four to reach ${DAILY_XP_GOAL} XP.`
              : totalXp >= DAILY_XP_GOAL
                ? "Daily goal reached. Wonderful consistency!"
                : `${DAILY_XP_GOAL - totalXp} XP to go to reach today's goal.`}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ==========================================
   IMAGE 1: MILESTONE ACHIEVEMENTS COMPONENT
   ========================================== */
const MilestoneShieldBadge = ({ badgeType, achieved }) => {
  const configs = {
    bronze: {
      shieldBorder: "#b45309",
      bgGrad: "from-amber-900/60 to-amber-950/80",
      subLabel: "15",
      badgeNum: "1",
      icon: "🏅"
    },
    silver: {
      shieldBorder: "#64748b",
      bgGrad: "from-slate-800/80 to-slate-900/80",
      subLabel: "30",
      badgeNum: "2",
      icon: "🥈"
    },
    gold: {
      shieldBorder: "#eab308",
      bgGrad: "from-yellow-950/60 to-amber-950/80",
      subLabel: "45",
      badgeNum: "1",
      icon: "🥇"
    },
    platinum: {
      shieldBorder: "#06b6d4",
      bgGrad: "from-cyan-950/60 to-teal-950/80",
      subLabel: "60",
      badgeNum: "60",
      icon: "💿"
    },
    diamond: {
      shieldBorder: "#3b82f6",
      bgGrad: "from-blue-950/60 to-slate-950/80",
      subLabel: "90",
      badgeNum: "90",
      icon: "💎"
    }
  };

  const cfg = configs[badgeType] || configs.bronze;

  return (
    <div className="flex flex-col items-center group cursor-pointer transition-transform duration-300 hover:scale-105">
      <div className="relative w-9 h-11 sm:w-14 sm:h-16 md:w-16 md:h-[4.5rem] flex flex-col items-center justify-center filter drop-shadow-[0_6px_10px_rgba(0,0,0,0.5)]">
        <svg viewBox="0 0 64 74" className="absolute inset-0 w-full h-full overflow-visible">
          <defs>
            <linearGradient id={`ringGrad-${badgeType}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={badgeType === 'bronze' ? '#f59e0b' : badgeType === 'silver' ? '#cbd5e1' : badgeType === 'gold' ? '#fde047' : badgeType === 'platinum' ? '#67e8f9' : '#60a5fa'} />
              <stop offset="100%" stopColor={badgeType === 'bronze' ? '#78350f' : badgeType === 'silver' ? '#475569' : badgeType === 'gold' ? '#b45309' : badgeType === 'platinum' ? '#0e7490' : '#1e40af'} />
            </linearGradient>
          </defs>

          <path
            d="M 32 4 L 58 12 C 58 38 48 58 32 70 C 16 58 6 38 6 12 Z"
            fill="#0f172a"
            stroke={`url(#ringGrad-${badgeType})`}
            strokeWidth="3.5"
            strokeLinejoin="round"
          />
          <path
            d="M 32 8 L 54 15 C 54 36 45 54 32 64 C 19 54 10 36 10 15 Z"
            fill="none"
            stroke={achieved ? cfg.shieldBorder : "#334155"}
            strokeWidth="1.5"
            opacity="0.6"
          />
        </svg>

        <div className="relative z-10 flex flex-col items-center justify-center -mt-1">
          <div className={`w-5 h-5 sm:w-8 sm:h-8 rounded-full border sm:border-2 border-white/40 flex items-center justify-center shadow-inner text-[10px] sm:text-sm bg-gradient-to-b ${cfg.bgGrad}`}>
            <span className="drop-shadow">{cfg.icon}</span>
          </div>
          <span className="text-[8px] sm:text-[10px] font-black text-white mt-0.5 tracking-tighter drop-shadow-md leading-none">{cfg.subLabel}</span>
        </div>
      </div>
    </div>
  );
};

const MilestoneAchievementsCard = () => {
  const cardTheme = "light";

  const milestoneData = [
    { target: 15, badgeType: "bronze", level: 1, times: 12.0, color: "from-amber-500 via-amber-400 to-yellow-300", glow: "rgba(245, 158, 11, 0.4)", statusText: "ACHIEVED", countText: "12 TIMES", achieved: true },
    { target: 30, badgeType: "silver", level: 2, times: 5.0, color: "from-slate-400 via-slate-300 to-slate-200", glow: "rgba(148, 163, 184, 0.4)", statusText: "ACHIEVED", countText: "5 TIMES", achieved: true },
    { target: 45, badgeType: "gold", level: 1, times: 2.0, color: "from-amber-500 via-yellow-400 to-amber-300", glow: "rgba(251, 191, 36, 0.4)", statusText: "ACHIEVED", countText: "2 TIMES", achieved: true },
    { target: 60, badgeType: "platinum", level: 60, times: 1.0, color: "from-teal-500 via-cyan-400 to-teal-300", glow: "rgba(6, 182, 212, 0.4)", statusText: "ACHIEVED", countText: "1 TIMES", achieved: true },
    { target: 90, badgeType: "diamond", level: 90, times: 0, color: "from-gray-300 to-gray-400", glow: "rgba(0,0,0,0)", statusText: "NOT ACHIEVED", countText: "YET", achieved: false },
  ];

  // Y axis = streak days (milestone length); bar height follows the milestone's streak days.
  const yAxisTicks = [90, 75, 60, 45, 30, 15, 0].map((v) => ({ label: String(v), val: v }));

  const maxY = 90;
  const isDark = cardTheme === "dark";

  return (
    <div className={`rounded-[28px] border transition-all duration-300 p-4 sm:p-6 md:p-8 relative overflow-hidden ${isDark
        ? "bg-gradient-to-br from-[#0c1322] via-[#10192d] to-[#090d18] border-[#1e2d4a] text-white shadow-[0_25px_60px_rgba(0,0,0,0.5)]"
        : "bg-white border-[#e8efe3] text-[#22331b] shadow-[0_10px_30px_rgba(80,105,67,0.06)]"
      }`}>

      {/* Background Cyan / Sage Light Wave Lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-25" viewBox="0 0 1000 500" preserveAspectRatio="none">
        <path d="M 0 250 Q 250 150, 500 280 T 1000 220" fill="none" stroke={isDark ? "#38bdf8" : "#7d9667"} strokeWidth="2" filter="blur(2px)" />
        <path d="M 0 300 Q 300 200, 600 320 T 1000 180" fill="none" stroke={isDark ? "#22d3ee" : "#00a896"} strokeWidth="1.5" />
      </svg>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative z-10 mb-4 sm:mb-8">
        <div>
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-[#7d9667]"
            }`}>MentalSwasthya • Rewards</span>
          <h2 className={`text-xl sm:text-2xl md:text-3xl font-black tracking-wide sm:tracking-wider uppercase break-words ${isDark ? "text-[#38bdf8] drop-shadow-[0_2px_10px_rgba(56,189,248,0.3)]" : "text-[#22331b]"
            }`}>
            MILESTONE ACHIEVEMENTS
          </h2>
          <p className={`text-[11px] sm:text-xs font-bold uppercase tracking-[0.08em] sm:tracking-[0.14em] mt-1 ${isDark ? "text-slate-400" : "text-[#66785c]"
            }`}>
            SUMMARY OF MILESTONES REACHED (TIMES COMPLETED)
          </p>
        </div>

        {/* Theme Toggle */}
        {/* <button
          type="button"
          onClick={() => setCardTheme(isDark ? "light" : "dark")}
          className={`self-start md:self-auto px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all ${
            isDark
              ? "bg-slate-800 border-slate-700 text-slate-300 hover:text-white"
              : "bg-[#eef6ea] border-[#7d9667]/20 text-[#7d9667] hover:bg-[#7d9667] hover:text-white"
          }`}
        >
          {isDark ? "☀️ Light Theme" : "🌙 Dark Theme"}
        </button> */}
      </div>

      {/* Chart Main Area */}
      <div className="relative z-10 flex gap-1.5 sm:gap-2 md:gap-3 items-start pt-12 pb-1 px-0 sm:px-2">

        {/* Y-Axis Title */}
        <div className={`shrink-0 h-[240px] flex items-center justify-center text-[10px] sm:text-xs font-black uppercase tracking-[0.15em] ${isDark ? "text-slate-400" : "text-[#66785c]"
          }`} style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          Streak days
        </div>

        {/* Y-Axis Column */}
        <div className={`w-6 sm:w-10 shrink-0 flex flex-col justify-between h-[240px] text-right pr-1 sm:pr-2 font-extrabold text-[10px] sm:text-[11px] md:text-xs ${isDark ? "text-slate-400" : "text-[#66785c]"
          }`}>
          {yAxisTicks.map((tick, idx) => (
            <span key={idx} className="leading-none whitespace-nowrap -my-[5px]">
              {tick.label}
            </span>
          ))}
        </div>

        {/* Chart Column (canvas + numbers) */}
        <div className="flex-1 min-w-0">
        {/* Chart Column Canvas */}
        <div className={`relative h-[240px] border-l border-b ${isDark ? "border-slate-700/80" : "border-[#e8efe3]"
          }`}>

          {/* Horizontal Gridlines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {yAxisTicks.map((_, idx) => (
              <div key={idx} className={`w-full border-b ${isDark ? "border-slate-800/60" : "border-gray-100"}`} />
            ))}
          </div>

          {/* Milestone Bars: height = streak days of the milestone */}
          <div className="absolute inset-0 grid grid-cols-5 gap-1.5 sm:gap-2 md:gap-6 px-1.5 sm:px-2 md:px-6">
            {milestoneData.map((item, index) => {
              const heightPct = Math.min(100, (item.target / maxY) * 100);

              return (
                <div key={index} className="relative h-full min-w-0 flex justify-center group">
                  <div
                    className={`absolute bottom-0 w-full max-w-[64px] rounded-t-lg ${item.achieved ? `bg-gradient-to-t ${item.color}` : isDark ? "bg-slate-800/70" : "bg-gray-200"}`}
                    style={{
                      height: `${heightPct}%`,
                      boxShadow: item.achieved ? `0 0 15px ${item.glow}` : "none",
                      opacity: item.achieved ? 1 : 0.7,
                    }}
                    title={`${item.target}-day streak`}
                  />

                  {/* Shield Badge sits on top of its bar */}
                  <div className="absolute z-20" style={{ bottom: `calc(${heightPct}% + 4px)` }}>
                    <MilestoneShieldBadge
                      badgeType={item.badgeType}
                      achieved={item.achieved}
                    />
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* X axis: only the number of times each milestone was achieved */}
        <div className="grid grid-cols-5 gap-1.5 sm:gap-2 md:gap-6 px-1.5 sm:px-2 md:px-6 mt-2">
          {milestoneData.map((item, index) => (
            <div
              key={index}
              className={`text-center text-sm sm:text-base md:text-lg font-black ${item.achieved ? "text-[#22331b]" : isDark ? "text-slate-500" : "text-gray-400"}`}
            >
              {Math.round(item.times)}
            </div>
          ))}
        </div>
        </div>

      </div>

      <p className={`relative z-10 mt-1 text-center text-[10px] sm:text-xs font-black uppercase tracking-[0.15em] ${isDark ? "text-slate-400" : "text-[#66785c]"}`}>
        No of Times Achieved
      </p>

    </div>
  );
};

// Semi-circle gauge: streak freezes left (of the plan allowance) beside the current streak number.
const StreakFreezeGauge = ({ streak, freezesLeft, freezeCap }) => {
  const cap = Math.max(freezeCap, freezesLeft, 1);
  const pct = Math.min(freezesLeft / cap, 1);
  const arcLen = Math.PI * 40; // semicircle of radius 40
  return (
    <div className="flex items-center justify-center gap-5 sm:gap-8 rounded-2xl bg-[#f7faf5] border border-[#e6efe0] px-4 py-4">
      <div className="relative w-32 h-[72px]" role="img" aria-label={`${freezesLeft} of ${cap} streak freezes available`}>
        <svg viewBox="0 0 100 55" className="w-full h-full">
          <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke="#e5ebe0" strokeWidth="9" strokeLinecap="round" />
          <path
            d="M10 50 A40 40 0 0 1 90 50"
            fill="none"
            stroke="#2b93b3"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={`${arcLen * pct} ${arcLen}`}
            style={{ transition: "stroke-dasharray 0.8s ease" }}
          />
        </svg>
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center leading-none">
          <span className="text-xl font-black text-[#22331b]">{freezesLeft}</span>
          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">/ {cap} freezes</span>
        </div>
      </div>
      <div className="text-left">
        <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Streak Freeze</p>
        <p className="text-2xl font-black text-[#8a5a1f] mt-1 flex items-center gap-1.5">
          <span aria-hidden="true">🔥</span>
          {streak}
          <span className="text-xs font-bold text-gray-400">day streak</span>
        </p>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Arrived right after finishing a wellness tool: if the user doesn't pick anything within 4 seconds,
  // guide them to the last page (today's progress + share). Any interaction cancels it.
  useEffect(() => {
    if (!location.state?.fromTool) return undefined;
    const cancel = () => clearTimeout(timer);
    const timer = setTimeout(() => {
      navigate("/app/complete", { replace: true });
    }, 4000);
    const events = ["pointerdown", "keydown", "wheel", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, cancel, { once: true, passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((ev) => window.removeEventListener(ev, cancel));
    };
  }, [location.state, navigate]);

  const [history, setHistory] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [selectedDate, setSelectedDate] = useState("");
  const [streakInfo, setStreakInfo] = useState({ current: 0, freezesLeft: 0, freezeCap: getFreezeAllowance() });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchHistory = () => {
    setLoading(true);
    setError("");

    getBreathingHistory()
      .then((response) => setHistory(getHistoryPayload(response)))
      .catch(() => {
        setHistory([]);
        setError("Unable to load breathing history. Please check the API connection.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchHistory();

    getUserStreak()
      .then((res) => {
        const data = res?.data?.data || {};
        const streak = data.streak || data;
        const left = streak.streakFreezes ?? streak.freezesAvailable ?? streak.freezeCount ?? streak.freezes ?? 0;
        setStreakInfo({
          current: streak.currentStreak ?? 0,
          freezesLeft: Number(left) || 0,
          freezeCap: Number(streak.freezeLimit ?? streak.maxFreezes) || getFreezeAllowance(),
        });
      })
      .catch(() => { /* streak is optional on the dashboard; keep defaults */ });
    
    try {
      const todayStr = new Date().toDateString();
      const loginDays = JSON.parse(localStorage.getItem("mentalswasthya_login_days") || "[]");
      if (!loginDays.includes(todayStr)) {
        loginDays.push(todayStr);
        localStorage.setItem("mentalswasthya_login_days", JSON.stringify(loginDays));
      }
    } catch (e) {}
  }, []);

  const completedHistory = useMemo(
    () => history.filter((session) => session.isCompleted),
    [history]
  );

  const filteredHistory = useMemo(() => {
    if (selectedDate) {
      return completedHistory.filter((session) => {
        const dateValue = session.completedAt || session.createdAt;
        return toDateInputValue(dateValue) === selectedDate;
      });
    }

    const range = getPeriodDateRange(selectedPeriod);
    if (!range) return completedHistory;

    return completedHistory.filter((session) => {
      const dateValue = session.completedAt || session.createdAt;
      if (!dateValue) return false;
      const d = new Date(dateValue);
      return d >= range.start && d <= range.end;
    });
  }, [completedHistory, selectedDate, selectedPeriod]);

  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);

  const statsData = useMemo(() => {
    const range = getPeriodDateRange(selectedPeriod);

    const isInRange = (dateValue) => {
      if (!dateValue) return false;
      if (selectedDate) return toDateInputValue(dateValue) === selectedDate;
      if (!range) return true;
      const d = new Date(dateValue);
      return d >= range.start && d <= range.end;
    };

    // 1. Intentions Stats
    let intentionsCount = 0;
    let totalIntentions = 0;
    try {
      const intentions = JSON.parse(localStorage.getItem("mentalSwasthya:intentions") || "[]");
      const filteredIntentions = intentions.filter(item => isInRange(item.date || item.timestamp));
      totalIntentions = filteredIntentions.length;
      const uniqueIntentionDays = new Set(
        filteredIntentions.map(item => {
          const d = new Date(item.date || item.timestamp || Date.now());
          return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        })
      );
      intentionsCount = uniqueIntentionDays.size;
    } catch (e) {
      console.error(e);
    }
    const intentionsProgress = intentionsCount;

    // 2. Breathing Stats
    const filteredBreathing = completedHistory.filter(session => isInRange(session.completedAt || session.createdAt));
    const totalSeconds = filteredBreathing.reduce(
      (total, session) => total + (session.durationSeconds || 0),
      0
    );
    const breathingDays = new Set(
      filteredBreathing.map((session) => toDateInputValue(session.completedAt || session.createdAt))
    ).size;
    const totalBreathingSessions = filteredBreathing.length;

    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const breathingTimeFormatted = hrs > 0 ? `${hrs}h ${mins}m ${secs}s` : `${mins}m ${secs}s`;

    // 3. Play History (Meditation & Affirmation)
    let playHistory = [];
    try {
      playHistory = JSON.parse(localStorage.getItem("mentalswasthya_play_history") || "[]");
    } catch (e) {
      console.error(e);
    }

    const filteredPlayHistory = playHistory.filter(item => isInRange(item.timestamp));

    const getUniqueDaysForType = (type) => {
      const filtered = filteredPlayHistory.filter(item => item.type === type);
      const uniqueDays = new Set(
        filtered.map(item => {
          const d = new Date(item.timestamp || Date.now());
          return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        })
      );
      return uniqueDays.size;
    };

    let meditationDays = getUniqueDaysForType("meditation");
    let affirmationDays = getUniqueDaysForType("affirmation");

    const totalAudioSessions = filteredPlayHistory.length;

    const displayIntentions = totalIntentions;
    const displayBreathing = totalBreathingSessions;
    const displayAudio = totalAudioSessions;
    const totalPractices = displayIntentions + displayBreathing + displayAudio;

    return {
      intentionsDays: intentionsProgress,
      breathingDays,
      breathingTime: breathingTimeFormatted,
      breathingHrs: hrs,
      breathingMins: mins,
      meditationDays,
      affirmationDays,
      totalIntentions: displayIntentions,
      totalBreathingSessions: displayBreathing,
      totalAudioSessions: displayAudio,
      totalPractices
    };
  }, [completedHistory, selectedPeriod, selectedDate]);

  // Completed practices today (always "today", independent of the period filter)
  const dailyXpCounts = useMemo(() => {
    const today = toDateInputValue(Date.now());
    const isToday = (value) => {
      if (value === undefined || value === null || value === "") return false;
      const time = new Date(value).getTime();
      return !Number.isNaN(time) && toDateInputValue(time) === today;
    };

    let intention = 0;
    try {
      const intentions = JSON.parse(localStorage.getItem("mentalSwasthya:intentions") || "[]");
      // Intentions store a localized display date; fall back to the numeric id (Date.now()) when it can't be parsed
      intention = intentions.filter((item) => {
        const parsed = new Date(item.timestamp || item.date).getTime();
        return isToday(Number.isNaN(parsed) ? item.id : parsed);
      }).length;
    } catch (e) {
      console.error(e);
    }

    let playHistory = [];
    try {
      playHistory = JSON.parse(localStorage.getItem("mentalswasthya_play_history") || "[]");
    } catch (e) {
      console.error(e);
    }
    const todaysPlays = playHistory.filter((item) => isToday(item.timestamp));

    return {
      intention,
      breathing: completedHistory.filter((session) => isToday(session.completedAt || session.createdAt)).length,
      meditation: todaysPlays.filter((item) => item.type === "meditation").length,
      affirmation: todaysPlays.filter((item) => item.type === "affirmation").length,
    };
  }, [completedHistory]);

  // Web application usage, bucketed to match the selected period:
  // weekdays (today / weeks), days of the month (months), months (3 months / year) or years (all time).
  const usageChart = useMemo(() => {
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const shortDays = ["S", "M", "T", "W", "T", "F", "S"];
    const range = getPeriodDateRange(selectedPeriod);
    const now = new Date();

    let mode = "weekday";
    if (!selectedDate) {
      if (selectedPeriod === "this_month" || selectedPeriod === "last_month") mode = "monthday";
      else if (selectedPeriod === "last_3_months" || selectedPeriod === "this_year") mode = "month";
      else if (selectedPeriod === "all") mode = "year";
    }

    const toDate = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    // Collect every usage event as { date, minutes, login }
    const events = [];
    completedHistory.forEach((session) => {
      const d = toDate(session.completedAt || session.createdAt);
      if (d) events.push({ d, minutes: (session.completedSeconds || session.duration || 0) / 60 });
    });
    try {
      JSON.parse(localStorage.getItem("mentalswasthya_play_history") || "[]").forEach((session) => {
        const d = toDate(session.timestamp);
        if (d) events.push({ d, minutes: (session.duration || 0) / 60 });
      });
    } catch (e) { }
    try {
      JSON.parse(localStorage.getItem("mentalSwasthya:intentions") || "[]").forEach((item) => {
        const d = toDate(item.date || item.timestamp);
        if (d) events.push({ d, minutes: 1 });
      });
    } catch (e) { }
    try {
      JSON.parse(localStorage.getItem("mentalswasthya_login_days") || "[]").forEach((dateStr) => {
        const d = toDate(dateStr);
        if (d) events.push({ d, minutes: 0, login: true });
      });
    } catch (e) { }

    const inRange = (d) => {
      if (selectedDate) return toDateInputValue(d) === selectedDate;
      if (!range) return true;
      return d >= range.start && d <= range.end;
    };

    // Build the buckets
    let buckets = [];
    let bucketIndex;
    let xAxisLabel = "Days";
    if (mode === "weekday") {
      buckets = shortDays.map((label, i) => ({ label, fullDay: dayNames[i] }));
      bucketIndex = (d) => d.getDay();
    } else if (mode === "monthday") {
      const ref = range ? range.start : now;
      const daysInMonth = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
      buckets = Array.from({ length: daysInMonth }, (_, i) => ({
        label: [1, 10, 20, 30].includes(i + 1) ? String(i + 1) : "",
        fullDay: `${MONTHS[ref.getMonth()]} ${i + 1}`,
      }));
      bucketIndex = (d) => d.getDate() - 1;
    } else if (mode === "month") {
      xAxisLabel = "Months";
      const startMonth = selectedPeriod === "this_year" ? 0 : range.start.getMonth();
      const startYear = range.start.getFullYear();
      const total =
        selectedPeriod === "this_year"
          ? 12
          : (now.getFullYear() - startYear) * 12 + now.getMonth() - startMonth + 1;
      buckets = Array.from({ length: total }, (_, i) => {
        const m = (startMonth + i) % 12;
        const y = startYear + Math.floor((startMonth + i) / 12);
        return { label: MONTHS[m][0], fullDay: `${MONTHS[m]} ${y}` };
      });
      bucketIndex = (d) => (d.getFullYear() - startYear) * 12 + d.getMonth() - startMonth;
    } else {
      xAxisLabel = "Year";
      const years = events.map((e) => e.d.getFullYear());
      const firstYear = Math.min(now.getFullYear(), ...years);
      buckets = Array.from({ length: now.getFullYear() - firstYear + 1 }, (_, i) => ({
        label: String(firstYear + i).slice(-2),
        fullDay: String(firstYear + i),
      }));
      bucketIndex = (d) => d.getFullYear() - firstYear;
    }

    const counts = buckets.map(() => 0);
    const logins = buckets.map(() => false);
    events.forEach(({ d, minutes, login }) => {
      if (!inRange(d)) return;
      const i = bucketIndex(d);
      if (i < 0 || i >= buckets.length) return;
      counts[i] += minutes;
      if (login) logins[i] = true;
    });

    const maxVal = Math.max(...counts, 1);
    // Y-axis in 5-minute steps (0-5-10-15-20); grows in multiples of 5 only if usage exceeds 20 min
    const yStep = Math.max(5, Math.ceil(maxVal / 4 / 5) * 5);
    const niceMax = yStep * 4;
    const yTicks = [4, 3, 2, 1, 0].map((n) => n * yStep);

    return {
      xAxisLabel,
      yMax: niceMax,
      yTicks,
      data: buckets.map((b, i) => ({
        ...b,
        count: Math.round(counts[i]),
        didLogin: logins[i],
        percentage: counts[i] > 0 ? (counts[i] / niceMax) * 100 : 0,
      })),
    };
  }, [completedHistory, selectedPeriod, selectedDate]);
  const weeklyData = usageChart.data;

  return (
    <div className="space-y-4 sm:space-y-6 text-[#22331b] min-w-0">
      <section className="relative rounded-[28px] bg-gradient-to-br from-[#f5faf2] via-white to-[#eef7fb] border border-[#e1eadb] shadow-[0_18px_50px_rgba(80,105,67,0.08)] p-5 sm:p-7 md:p-9 z-20">
        <div className="absolute inset-0 rounded-[28px] overflow-hidden pointer-events-none">
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#7d9667]/10" />
          <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-sky-200/20" />
        </div>
        <p className="relative text-[11px] font-black uppercase tracking-[0.16em] text-[#7d9667]">
          MentalSwasthya
        </p>
        <div className="relative mt-3 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
          <div>
            <h1 className="text-[1.75rem] leading-tight sm:text-3xl md:text-4xl font-black tracking-tight text-[#22331b] break-words">
              Your wellness dashboard
            </h1>
            <p className="text-sm text-[#66785c] mt-2 max-w-2xl leading-6">
              Review your breathing practice, track completed sessions, and filter statistics by period.
            </p>
          </div>

          {/* Global Period Filter Custom Dropdown */}
          <div className="self-start xl:self-auto flex items-center gap-3 bg-white/90 backdrop-blur-md p-2.5 rounded-2xl border border-[#e1eadb] shadow-sm max-w-full">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-wider text-[#7d9667]">Period:</span>
              <CustomPeriodDropdown
                selectedPeriod={selectedPeriod}
                onSelectPeriod={(periodId) => {
                  setSelectedPeriod(periodId);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">

        {/* Left: 4 Circular Gauges */}
        <div className="rounded-3xl bg-white border border-[#e8efe3] p-4 sm:p-6 shadow-[0_10px_30px_rgba(80,105,67,0.06)] flex flex-col justify-between lg:col-span-3 min-h-[300px] min-w-0">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#7d9667]">Statistics</p>
            <h3 className="text-lg font-black text-[#22331b] mt-0.5">Wellness Overview</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 items-center justify-around gap-4 my-5 sm:my-6">

            {/* Circle 1: Daily Intention (Teal) */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f3f4f6" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#e3a92b" strokeWidth="3"
                    strokeDasharray={`${(statsData.intentionsDays / 31) * 100} 100`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 0.8s ease" }} />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-lg font-black text-[#22331b]">{statsData.intentionsDays}</span>
                  <span className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">/ 31 Days</span>
                </div>
              </div>
              <span className="text-[10px] font-black text-gray-500 mt-2">INTENTIONS</span>
            </div>

            {/* Circle 2: Guided Breathing (Blue) */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f3f4f6" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#2b93b3" strokeWidth="3"
                    strokeDasharray={`${(statsData.breathingDays / 7) * 100} 100`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 0.8s ease" }} />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-lg font-black text-[#22331b]">{statsData.breathingDays}</span>
                  <span className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">/ 7 Days</span>
                </div>
              </div>
              <span className="text-[10px] font-black text-gray-500 mt-2">BREATHING</span>
            </div>

            {/* Circle 3: Mindful Meditation (Purple) */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f3f4f6" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#6a3f8c" strokeWidth="3"
                    strokeDasharray={`${(statsData.meditationDays / 7) * 100} 100`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 0.8s ease" }} />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-lg font-black text-[#22331b]">{statsData.meditationDays}</span>
                  <span className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">/ 7 Days</span>
                </div>
              </div>
              <span className="text-[10px] font-black text-gray-500 mt-2">MEDITATION</span>
            </div>

            {/* Circle 4: Daily Affirmation (Orange) */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f3f4f6" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke="#4a7f2e" strokeWidth="3"
                    strokeDasharray={`${(statsData.affirmationDays / 7) * 100} 100`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 0.8s ease" }} />
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-lg font-black text-[#22331b]">{statsData.affirmationDays}</span>
                  <span className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">/ 7 Days</span>
                </div>
              </div>
              <span className="text-[10px] font-black text-gray-500 mt-2">AFFIRMATIONS</span>
            </div>

          </div>

          <StreakFreezeGauge
            streak={streakInfo.current}
            freezesLeft={streakInfo.freezesLeft}
            freezeCap={streakInfo.freezeCap}
          />

          <div className="flex justify-center border-t border-gray-100/60 pt-3">
            <span className="bg-[#f4f8f1] border border-[#e1eadb] rounded-full px-4 py-1 text-[11px] sm:text-xs font-black uppercase text-[#66785c] tracking-wide sm:tracking-wider text-center max-w-full">
              TOTALS • Out of {statsData.totalPractices} wellness practices
            </span>
          </div>
        </div>

        {/* Right: completions bar chart */}
        <div className="rounded-3xl bg-white border border-[#e8efe3] p-4 sm:p-6 shadow-[0_10px_30px_rgba(80,105,67,0.06)] flex flex-col justify-between lg:col-span-2 relative min-h-[260px] sm:min-h-[300px] min-w-0">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#7d9667]">Activity</p>
            <h3 className="text-lg font-black text-[#22331b] mt-0.5">WebApplication Usage</h3>
          </div>

          <div className="flex gap-2 mt-4 mb-2">
            {/* Y axis */}
            <div className="flex items-stretch gap-1.5 shrink-0">
              <span
                className="text-[9px] font-bold uppercase tracking-wider text-gray-400 self-center"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                Usage (minutes)
              </span>
              <div className="h-36 flex flex-col justify-between text-[9px] font-bold text-gray-400 text-right tabular-nums">
                {usageChart.yTicks.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="relative h-36 flex items-end justify-between gap-1 px-1">
                {hoveredBarIndex !== null && weeklyData[hoveredBarIndex] && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-900/90 text-white rounded-xl px-3.5 py-1.5 text-[10px] font-bold shadow-xl border border-gray-800 z-20 backdrop-blur-sm pointer-events-none transition-all">
                    {weeklyData[hoveredBarIndex].fullDay}: <span className="text-[#a8c896]">{weeklyData[hoveredBarIndex].count} mins</span>
                  </div>
                )}

                {weeklyData.map((item, index) => (
                  <div
                    key={index}
                    className="flex-1 min-w-0 flex flex-col items-center h-full justify-end group cursor-pointer relative"
                    onMouseEnter={() => setHoveredBarIndex(index)}
                    onMouseLeave={() => setHoveredBarIndex(null)}
                  >
                    <div className="absolute inset-x-0.5 top-0 bottom-0 bg-gray-100 rounded-md -z-10 group-hover:bg-gray-200/80 transition-colors" />

                    <div
                      className="w-full max-w-[12px] rounded-t-md transition-all"
                      style={{
                        height: `${item.percentage}%`,
                        minHeight: item.count > 0 ? "10px" : (item.didLogin ? "10px" : "2px"),
                        backgroundColor: item.count === 0
                          ? (item.didLogin ? "#ef4444" : "#d1d5db")
                          : "#7d9667"
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between gap-1 px-1 mt-2">
                {weeklyData.map((item, index) => (
                  <span key={index} className="flex-1 min-w-0 text-center text-[9px] sm:text-[10px] font-black text-gray-400 uppercase whitespace-nowrap overflow-visible">
                    {item.label}
                  </span>
                ))}
              </div>
              <p className="text-center text-[9px] font-bold uppercase tracking-wider text-gray-400 mt-1">
                {usageChart.xAxisLabel}
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* Today's XP breakdown by practice */}
      <DailyXpCard counts={dailyXpCounts} />

      {/* Milestone Achievements Chart (Image 1) */}
      <MilestoneAchievementsCard />

      {/* <section className="rounded-[28px] bg-white border border-[#e8efe3] shadow-[0_10px_30px_rgba(80,105,67,0.06)] overflow-hidden">
        <div className="p-5 md:p-6 border-b border-[#e8efe3] bg-[#fbfdf8] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#7d9667]">
              Breathing History
            </p>
            <h2 className="text-xl font-black text-[#22331b] mt-1">Session records</h2>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={fetchHistory}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#7d9667] px-5 py-3 text-sm font-bold text-white hover:bg-[#6f865c] transition-all shadow-lg shadow-[#7d9667]/15"
            >
              <FiRefreshCcw />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead>
              <tr className="bg-[#f4f8f1] text-[11px] uppercase tracking-[0.12em] text-[#8a9a80]">
                <th className="px-6 py-4 font-black">Date</th>
                <th className="px-6 py-4 font-black">Technique</th>
                <th className="px-6 py-4 font-black">Shape</th>
                <th className="px-6 py-4 font-black">Duration</th>
                <th className="px-6 py-4 font-black">Cycles</th>
                <th className="px-6 py-4 font-black">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm font-semibold text-[#8a9a80]">
                    Loading breathing history...
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm font-semibold text-[#ce7a63]">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && filteredHistory.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm font-semibold text-[#8a9a80]">
                    No breathing sessions found for this date.
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                filteredHistory.map((session) => (
                  <tr key={session._id} className="hover:bg-[#fbfdf8] transition-colors">
                    <td className="px-6 py-4 text-sm font-semibold text-[#3a4a31]">
                      {formatDateTime(session.completedAt || session.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#66785c]">
                      {getTechniqueLabel(session.technique)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-[#eef6ea] px-3 py-1 text-xs font-bold capitalize text-[#7d9667]">
                        {session.shape || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-[#3a4a31]">
                      {formatDuration(session.durationSeconds || 0)}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-[#3a4a31]">
                      {session.cyclesCompleted || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-[#edf8f0] px-3 py-1 text-xs font-bold text-[#4c9a62]">
                        Completed
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section> */}
    </div>
  );
};

export default Dashboard;
  